/**
 * Utility functions for extracting hero images from post content
 */
import { getGooglePhotoProxyUrl, getGooglePhotoProxyUrlFromName } from '@/utils/googlePhotoProxy';
import { getPhotoUrl, type PhotoIdentifier } from '@/utils/photoUtils';
import { debug } from '@/utils/logger';

interface PostContent {
  post_type?: string | null;
  type?: string | null;
  hero_image_url?: string | null;
  cover_image?: string | null;
  card_image_url?: string | null;
  image_urls?: Array<string> | null;
  source_metadata?: {
    images?: string[] | null;
  } | null;
  post_locations?: Array<{
    image_urls?: Array<string> | null;
    location?: {
      photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
      google_places_data?: {
        photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
      } | null;
    } | null;
  }> | null;
  // Also support camelCase (Post model uses both)
  postLocations?: Array<{
    image_urls?: Array<string> | null;
    location?: {
      photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
      google_places_data?: {
        photos?: Array<{ name?: string; photo_reference?: string; url?: string } | string> | null;
      } | null;
    } | null;
  }> | null;
}

function normalizePotentialPhotoUrl(img: string | { url?: string } | PhotoIdentifier | null | undefined): string {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img !== 'object') return '';
  // Use `in` to narrow the union safely — PhotoIdentifier has no `url` field
  // and the legacy { url? } shape has neither `name` nor `photo_reference`.
  if ('url' in img && typeof img.url === 'string') return img.url;
  if (
    ('name' in img && typeof img.name === 'string') ||
    ('photo_reference' in img && typeof img.photo_reference === 'string')
  ) {
    return getPhotoUrl(img as PhotoIdentifier, 400);
  }
  return '';
}

export interface HeroImageResult {
  primary: string;
  fallback?: string; // Fallback URL if primary fails (e.g., Google Places photo if S3 image is missing)
}

/**
 * Extracts the hero image URL from a post content object.
 *
 * For list/itinerary/place/reply posts, follows this priority order:
 * 1. Direct post image fields (hero_image_url, cover_image, card_image_url)
 * 2. Post's own images array (image_urls) - excluding Instagram/TikTok CDN URLs
 * 3. PostLocation images (post_locations[].image_urls) - may be missing S3 images
 * 4. Location Google Places photos (post_locations[].location.photos and location.google_places_data.photos)
 *
 * Note: Instagram/TikTok CDN URLs are skipped because they're blocked for hotlinking.
 * For Instagram-imported posts, we fall back to location Google Places photos.
 *
 * For other post types, returns empty string.
 *
 * @param content - The post content object
 * @returns Object with primary URL and optional fallback URL. The fallback is used if primary fails (404).
 */
export const getHeroImageFromPost = (content: PostContent | null | undefined): HeroImageResult => {
  if (!content) return { primary: "" };

  const postType = (content.post_type || content.type || "").toString().toLowerCase();
  // Support list, itinerary, place, and reply post types (all can have locations with photos)
  const isSupportedType = postType.includes("list") || 
                         postType.includes("itinerary") || 
                         postType.includes("place") || 
                         postType.includes("reply");

  if (!isSupportedType) {
    debug('[getHeroImageFromPost] Post type not supported', { postType, contentId: (content as { _id?: string })?._id });
    return { primary: "" };
  }

  // Handle both snake_case and camelCase (Post model uses both)
  const postLocations = (content.post_locations || content.postLocations) || [];

  // 0. Check direct post image fields first (highest priority)
  const directImages = [
    content.hero_image_url,
    content.cover_image,
    content.card_image_url,
  ].filter(Boolean) as string[];

  if (directImages.length > 0) {
    return { primary: directImages[0] };
  }

  // Helper function to check if URL is from Instagram/TikTok CDN (blocked for hotlinking)
  const isBlockedCDNUrl = (url: string): boolean => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.includes('cdninstagram.com') ||
      lowerUrl.includes('instagram.com') ||
      (lowerUrl.includes('instagram.') && lowerUrl.includes('fbcdn.net')) ||
      lowerUrl.includes('fbcdn.net') ||
      lowerUrl.includes('tiktokcdn.com') ||
      (lowerUrl.includes('scontent') && (lowerUrl.includes('instagram') || lowerUrl.includes('fbcdn'))) ||
      lowerUrl.includes('scontent.cdninstagram.com')
    );
  };

  // Helper function to check if URL is a video file (should not be used with Next.js Image)
  const isVideoFile = (url: string): boolean => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.endsWith('.mp4') ||
      lowerUrl.endsWith('.mov') ||
      lowerUrl.endsWith('.webm') ||
      lowerUrl.endsWith('.avi') ||
      lowerUrl.includes('.mp4?') ||
      lowerUrl.includes('.mov?') ||
      lowerUrl.includes('.webm?')
    );
  };

  // 1. Post's own images array (but skip Instagram/TikTok CDN URLs - they're blocked, and skip video files)
  let postImageUrl: string | null = null;
  if (content.image_urls && content.image_urls.length > 0) {
    const firstImage = content.image_urls
      .map((img: string | { url?: string } | PhotoIdentifier) => normalizePotentialPhotoUrl(img))
      .filter(Boolean)
      .filter((url: string) => !isBlockedCDNUrl(url))
      .filter((url: string) => !isVideoFile(url))[0];

    if (firstImage) {
      // image_urls contains URLs (S3 or other), use as-is
      postImageUrl = firstImage;
    } else {
      // Check if we filtered out Instagram URLs - if so, we'll need Google Places fallback
      const hasBlockedUrls = content.image_urls
        .map((img: string | { url?: string } | PhotoIdentifier) => normalizePotentialPhotoUrl(img))
        .filter(Boolean)
        .some((url: string) => isBlockedCDNUrl(url));
      
      if (hasBlockedUrls) {
        debug('[getHeroImageFromPost] Post image_urls contains blocked Instagram/TikTok URLs, will use Google Places fallback', {
          postType,
          postId: (content as { _id?: string })?._id,
        });
      }
    }
  }

  // Helper function to extract photo URL from a photo object
  const extractPhotoFromObject = (photo: string | { name?: string; photo_reference?: string; url?: string } | null | undefined): string | null => {
    if (!photo) return null;
    
    // Handle string URLs (S3 or other URLs, not identifiers)
    if (typeof photo === "string") {
      return photo;
    }

    // Prefer new format with identifiers (backend sends these)
    if (photo.name && typeof photo.name === 'string' && !photo.name.startsWith('http')) {
      return getGooglePhotoProxyUrlFromName(photo.name, 400);
    }
    
    if (photo.photo_reference && !photo.photo_reference.startsWith('http')) {
      return getGooglePhotoProxyUrl(photo.photo_reference, 400);
    }
    
    return null;
  };

  // 2. Collect PostLocation images (S3 URLs - may be missing) and Google Places photos (fallback)
  let postLocationImageUrl: string | null = null;
  let googlePlacesPhotoUrl: string | null = null;

  if (postLocations.length > 0) {
    for (const pl of postLocations) {
      const location = pl?.location;

      // Collect PostLocation S3 image (primary - user-uploaded content, skip video files and blocked CDN URLs)
      if (!postLocationImageUrl && pl?.image_urls && pl.image_urls.length > 0) {
        const firstPLImage = pl.image_urls
          .map((img: string | { url?: string } | PhotoIdentifier) => normalizePotentialPhotoUrl(img))
          .filter(Boolean)
          .filter((url: string) => !isVideoFile(url))
          .filter((url: string) => !isBlockedCDNUrl(url))[0];
        
        if (firstPLImage) {
          postLocationImageUrl = firstPLImage;
        }
      }

      // Collect Google Places photo (fallback if S3 image is missing)
      if (!googlePlacesPhotoUrl && location) {
        // First check location.photos (direct photos array)
        if (location.photos && location.photos.length > 0) {
          const photoUrl = extractPhotoFromObject(location.photos[0]);
          if (photoUrl) {
            googlePlacesPhotoUrl = photoUrl;
          }
        }

        // Then check location.google_places_data.photos (where Google Places photos actually are)
        if (!googlePlacesPhotoUrl && location.google_places_data?.photos && location.google_places_data.photos.length > 0) {
          const photoUrl = extractPhotoFromObject(location.google_places_data.photos[0]);
          if (photoUrl) {
            googlePlacesPhotoUrl = photoUrl;
          }
        }
      }
    }
  }

  // Return priority: post.image_urls > PostLocation S3 images > Google Places photos
  // Always include Google Places photos as fallback if available
  if (postImageUrl) {
    return {
      primary: postImageUrl,
      fallback: googlePlacesPhotoUrl || undefined
    };
  }

  // Return primary (S3 image) with fallback (Google Places photo)
  // If S3 image exists, use it as primary; if it fails, useHeroImage will try fallback
  if (postLocationImageUrl) {
    return {
      primary: postLocationImageUrl,
      fallback: googlePlacesPhotoUrl || undefined
    };
  }

  // If no S3 image, use Google Places photo as primary
  if (googlePlacesPhotoUrl) {
    return { primary: googlePlacesPhotoUrl };
  }
  return { primary: "" };
};

export const getSecondaryHeroImageFromPost = (content: PostContent | null | undefined): string => {
  if (!content?.image_urls || content.image_urls.length === 0) {
    return "";
  }

  const firstImage = content.image_urls[0];

  return normalizePotentialPhotoUrl(firstImage as string | { url?: string } | PhotoIdentifier);
};

